/**
 * A realtime voice call: the phone talks to an OpenAI speech-to-speech session over WebRTC (mic up, voice down),
 * and the backend holds the other end (its sideband controller hands requests to the agent and has the voice
 * say the agent's replies; backend `app/harness/realtime.py`). The phone never holds an OpenAI key: its SDP offer
 * goes to `POST /voice/realtime/connect`, which makes the call and returns the answer.
 *
 * The data channel's events only drive the call screen here: who's talking (phases, captions), levels for the
 * orb, and the turn's latency marks. Everything that changes the conversation happens on the server.
 */
import { agentApi } from '@/agent/api'
import type { CallErrorKind } from '../types'

export interface RealtimeHandlers {
    /** The voice model heard the creator start / stop talking. */
    userSpeaking(on: boolean): void
    /** What the creator said (the model's transcription, after their turn). */
    userTranscript(text: string): void
    /** The voice's audio started / stopped playing; `relay`: it's saying the agent's words. */
    agentSpeaking(on: boolean, relay: boolean): void
    /** The voice's words so far in this response (captions). */
    agentTranscript(text: string): void
    /** A response handed the request to the agent (`delegate`). */
    delegated(): void
    /** The connection failed or dropped. */
    error(kind: CallErrorKind): void
}

export interface RealtimeConnection {
    readonly callId: string
    setMuted(on: boolean): void
    /** Tap to interrupt: stop what the voice is saying now. */
    interrupt(): void
    micLevel(): number
    outLevel(): number
    close(): void
}

export function realtimeSupported(): boolean {
    return typeof window !== 'undefined' && typeof RTCPeerConnection === 'function'
        && typeof navigator?.mediaDevices?.getUserMedia === 'function'
}

function meter(ctx: AudioContext | null, stream: MediaStream): () => number {
    if (!ctx) return () => 0
    try {
        const src = ctx.createMediaStreamSource(stream)
        const an = ctx.createAnalyser()
        an.fftSize = 512
        src.connect(an)
        const buf = new Float32Array(an.fftSize)
        return () => {
            an.getFloatTimeDomainData(buf)
            let sum = 0
            for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
            return Math.min(1, Math.sqrt(sum / buf.length) * 4)
        }
    } catch {
        return () => 0
    }
}

/** Open the call: mic, peer connection, the server's answer. Rejects (and releases everything) on failure. */
export async function connectRealtime(sessionId: string, h: RealtimeHandlers,
                                      opts: { context: () => AudioContext | null; timeoutMs?: number }): Promise<RealtimeConnection> {
    const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    const pc = new RTCPeerConnection()
    const audio = document.createElement('audio')
    audio.autoplay = true
    audio.setAttribute('playsinline', '')
    audio.style.display = 'none'
    document.body.appendChild(audio)
    let outMeter: () => number = () => 0
    const micMeter = meter(opts.context(), mic)
    let closed = false
    let callId = ''
    let relay = false
    let caption = ''

    const release = () => {
        if (closed) return
        closed = true
        try { dc.close() } catch { /* closed */ }
        try { pc.close() } catch { /* closed */ }
        for (const t of mic.getTracks()) t.stop()
        audio.srcObject = null
        audio.remove()
        if (callId) void agentApi().realtimeHangup(callId).catch(() => {})
    }

    pc.ontrack = (e) => {
        audio.srcObject = e.streams[0]
        outMeter = meter(opts.context(), e.streams[0])
    }
    for (const t of mic.getTracks()) pc.addTrack(t, mic)
    const dc = pc.createDataChannel('oai-events')
    dc.onmessage = (m) => {
        let ev: any
        try {
            ev = JSON.parse(typeof m.data === 'string' ? m.data : '')
        } catch {
            return
        }
        switch (ev?.type) {
            case 'input_audio_buffer.speech_started': h.userSpeaking(true); break
            case 'input_audio_buffer.speech_stopped': h.userSpeaking(false); break
            case 'conversation.item.input_audio_transcription.completed':
                if (ev.transcript) h.userTranscript(String(ev.transcript).trim())
                break
            case 'response.created':
                relay = ev.response?.metadata?.lh === 'relay'
                caption = ''
                break
            case 'response.output_audio_transcript.delta':
                caption += ev.delta ?? ''
                h.agentTranscript(caption)
                break
            case 'output_audio_buffer.started': h.agentSpeaking(true, relay); break
            case 'output_audio_buffer.stopped':
            case 'output_audio_buffer.cleared': h.agentSpeaking(false, relay); break
            case 'response.done':
                if ((ev.response?.output ?? []).some((o: any) => o?.type === 'function_call' && o?.name === 'delegate')) h.delegated()
                break
        }
    }
    pc.onconnectionstatechange = () => {
        if (closed) return
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed') {
            release()
            h.error('network')
        }
    }

    try {
        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)
        const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), opts.timeoutMs ?? 10_000))
        const answer = await Promise.race([agentApi().realtimeConnect({ session_id: sessionId, sdp: offer.sdp ?? '' }), timeout])
        callId = answer.call_id
        await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp })
    } catch (e) {
        release()
        throw e
    }

    return {
        get callId() { return callId },
        setMuted(on: boolean) {
            for (const t of mic.getAudioTracks()) t.enabled = !on
        },
        interrupt() {
            if (closed || dc.readyState !== 'open') return
            dc.send(JSON.stringify({ type: 'response.cancel' }))
            dc.send(JSON.stringify({ type: 'output_audio_buffer.clear' }))
        },
        micLevel: () => (closed ? 0 : micMeter()),
        outLevel: () => (closed ? 0 : outMeter()),
        close: release,
    }
}
